// ABOUTME: Renders the submitted fields and current processing status for RMC requests.
// It is the expandable detail area of the Orders and Deliveries status banner.
import React from 'react'
import {useTranslation} from 'react-i18next'

import {formattedDate} from '../../../../../Pages/Order/OrderDetails/components/utils'
import {formatQuantityDisplay} from '../../../shared/order-editor/utils/quantity.utils'
import {RmcOrderStatus} from '../../hooks/useRmcOrderStatuses'
import {TransactionResultType} from '../../types/confirmationResult'
import {RmcOrderRequestSummary} from '../../types/order'

interface RmcOrderStatusDetailsProps {
  requests: RmcOrderRequestSummary[]
  statuses: RmcOrderStatus[]
  isChecking: boolean
  isError: boolean
}

const StatusLabelKeys: Record<TransactionResultType, string> = {
  success: 'orderIntake.rmc.confirmation.chipLabels.confirmed',
  pending: 'orderIntake.rmc.confirmation.chipLabels.pending',
  failure: 'orderIntake.rmc.confirmation.chipLabels.orderFailed',
  pendingDispatch: 'orderIntake.rmc.confirmation.chipLabels.pendingDispatch',
  customerActionRequired: 'orderIntake.rmc.confirmation.chipLabels.customerActionRequired'
}

export const RmcOrderStatusDetails: React.FC<RmcOrderStatusDetailsProps> = ({
  requests,
  statuses,
  isChecking,
  isError
}) => {
  const {t, i18n} = useTranslation()

  const getStatusLabel = (requestId: string) => {
    const status = statuses.find((item) => item.requestId === requestId)?.status

    if (status) return t(StatusLabelKeys[status])
    if (isChecking) return t('orderIntake.rmc.confirmation.chipLabels.checking')
    if (isError) return t('orderIntake.rmc.confirmation.chipLabels.checkFailed')
    return t('orderIntake.rmc.confirmation.chipLabels.unknownStatus')
  }

  return (
    <div id="rmc-order-status-details" className="mt-3 border-t border-solid border-neutral-300">
      {requests.map((request) => (
        <dl
          key={request.requestId}
          className="grid grid-cols-1 gap-3 border-b border-solid border-neutral-300 py-3 md:grid-cols-5"
          data-test-id={`rmc-order-status-details-${request.requestId}`}
        >
          <div>
            <dt className="text-xs font-semibold">{t('orderIntake.rmc.statusBanner.material')}</dt>
            <dd className="text-sm">{request.material}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold">{t('orderIntake.rmc.statusBanner.quantity')}</dt>
            <dd className="text-sm">{formatQuantityDisplay(request.quantity, t)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold">{t('orderIntake.rmc.statusBanner.date')}</dt>
            <dd className="text-sm">{formattedDate(request.deliveryDate, i18n.language)[0]}</dd>
          </div>
          {request.poNumber && (
            <div>
              <dt className="text-xs font-semibold">
                {t('orderIntake.rmc.statusBanner.poNumber')}
              </dt>
              <dd className="text-sm">{request.poNumber}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-semibold">{t('orderIntake.rmc.statusBanner.status')}</dt>
            <dd className="text-sm">{getStatusLabel(request.requestId)}</dd>
          </div>
        </dl>
      ))}
    </div>
  )
}
